/**
 * <dtf-card-gallery> — DTF 商品カードの写真送り。
 * PC: 矢印で送る。SP: スワイプで送る（ホバーでは切り替えない）。
 * 画像が1枚のカードでは何もしない（矢印もドットもLiquid側で出力されない）。
 */
class DtfCardGallery extends HTMLElement {
  connectedCallback() {
    if (this._initialized) return;

    this.track = this.querySelector('[data-track]');
    this.images = [...this.querySelectorAll('.dtf-card__img')];
    if (this.images.length < 2 || !this.track) return;

    this._initialized = true;
    this.dots = [...this.querySelectorAll('[data-dot]')];
    this.status = this.querySelector('[data-status]');
    this.index = 0;

    this.prev = this.querySelector('[data-prev]');
    this.next = this.querySelector('[data-next]');
    this._onPrev = (event) => {
      event.preventDefault();
      event.stopPropagation();
      this.go(this.index - 1);
    };
    this._onNext = (event) => {
      event.preventDefault();
      event.stopPropagation();
      this.go(this.index + 1);
    };
    this.prev?.addEventListener('click', this._onPrev);
    this.next?.addEventListener('click', this._onNext);

    this._onKeyDown = (event) => {
      if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      event.preventDefault();
      this.go(this.index + (event.key === 'ArrowRight' ? 1 : -1));
    };
    this.addEventListener('keydown', this._onKeyDown);

    // スワイプ（縦スクロールを妨げないよう、横移動が明確なときだけ送る）
    this._onPointerDown = (event) => {
      this._suppressClick = false;
      clearTimeout(this._clickTimer);
      this._gesture = undefined;
      if (event.pointerType === 'mouse' || !event.isPrimary) return;
      if (!(event.target instanceof Element) || !event.target.closest('.dtf-card__link')) return;
      this._gesture = { id: event.pointerId, x: event.clientX, y: event.clientY };
    };
    this._onPointerUp = (event) => {
      if (!this._gesture || this._gesture.id !== event.pointerId) return;
      const dx = event.clientX - this._gesture.x;
      const dy = event.clientY - this._gesture.y;
      this._gesture = undefined;
      if (Math.abs(dx) < 40 || Math.abs(dx) <= Math.abs(dy) * 1.25) return;
      event.preventDefault();
      this.go(dx < 0 ? this.index + 1 : this.index - 1);
      // pointerup の後に生成される click で商品ページへ移動しないようにする。
      // 新しいタップやキーボードの Enter は通常どおり利用できる。
      this._suppressClick = true;
      this._clickTimer = setTimeout(() => {
        this._suppressClick = false;
      }, 500);
    };
    this._onPointerCancel = () => {
      this._gesture = undefined;
    };
    this._onClickCapture = (event) => {
      if (!this._suppressClick || event.detail === 0) return;
      if (!(event.target instanceof Element) || !event.target.closest('.dtf-card__link')) return;
      event.preventDefault();
      event.stopPropagation();
      this._suppressClick = false;
      clearTimeout(this._clickTimer);
    };
    this.addEventListener('pointerdown', this._onPointerDown, { passive: true });
    this.addEventListener('pointerup', this._onPointerUp);
    this.addEventListener('pointercancel', this._onPointerCancel);
    this.addEventListener('click', this._onClickCapture, true);
    this.show(this.index);
  }

  go(i) {
    const n = this.images.length;
    this.index = ((i % n) + n) % n;
    this.show(this.index);
  }

  show(i) {
    this.track.style.transform = `translateX(-${i * 100}%)`;
    this.dots.forEach((dot, n) => dot.classList.toggle('is-on', n === i));
    if (this.status) {
      this.status.textContent = this.status.dataset.statusTemplate.replace('__index__', String(i + 1));
    }
  }

  disconnectedCallback() {
    this.prev?.removeEventListener('click', this._onPrev);
    this.next?.removeEventListener('click', this._onNext);
    this.removeEventListener('keydown', this._onKeyDown);
    this.removeEventListener('pointerdown', this._onPointerDown);
    this.removeEventListener('pointerup', this._onPointerUp);
    this.removeEventListener('pointercancel', this._onPointerCancel);
    this.removeEventListener('click', this._onClickCapture, true);
    clearTimeout(this._clickTimer);
    this._gesture = undefined;
    this._suppressClick = false;
    this._initialized = false;
  }
}

if (!customElements.get('dtf-card-gallery')) {
  customElements.define('dtf-card-gallery', DtfCardGallery);
}
